import {
  IActivityObject,
  IBooking,
  ICoPlayerBooking,
  MemberOccupancyRow,
  MemberStatisticsExport,
} from "../../../../types/api";
import { createStatisticsFilterStore } from "../../Stores/statisticsFilterStoreFactory";

const createRowsForSpecificMember = (
  memberOccupancyFilterStore: ReturnType<typeof createStatisticsFilterStore>,
  bookings: IBooking[],
  coPlayerBookings: ICoPlayerBooking[],
  activityBookings: IActivityObject[],
  member_name: string,
  member_id: string,
  memberType: string,
  startDate: string,
  endDate: string,
  weekdayCounts: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  }
): MemberStatisticsExport => {
  const weekdays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ] as const;

  const getISOYearWeek = (d: Date) => {
    const date = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    );
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));

    const isoYear = date.getUTCFullYear();
    const yearStart = new Date(Date.UTC(isoYear, 0, 1));
    const isoWeek = Math.ceil(
      ((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
    );

    return { isoYear, isoWeek };
  };

  // --- Create map for counting bookings per week/day ---
  type WeekKey = string; // "2025-52"
  type WeekMap = Record<
    WeekKey,
    Record<
      (typeof weekdays)[number],
      { booker: number; participant: number; activity: number }
    >
  >;

  const weekMap: WeekMap = {};
  const increment = (
    date: Date,
    weekday: (typeof weekdays)[number],
    field: "booker" | "participant" | "activity"
  ) => {
    const { isoYear, isoWeek } = getISOYearWeek(date);
    const key = `${isoYear}-${isoWeek}`;

    weekMap[key] ??= {} as any;
    weekMap[key][weekday] ??= { booker: 0, participant: 0, activity: 0 };
    weekMap[key][weekday][field] += 1;
  };

  const { startTime, endTime } = memberOccupancyFilterStore.getState();
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T23:59:59Z`);

  const inRange = (date: Date) => date >= start && date <= end;
  const isInTimeRange = (time: string) => {
    return time >= startTime && time < endTime;
  };
  const validStatuses = [
    "PAID",
    "PAID_NOT_CHECKED_IN",
    "CONFIRMED",
    "CHECKED_IN",
  ];
  // --- Add bookings if within range ---
  for (const b of bookings) {
    const date = new Date(b.datetime_start);
    const timeStr = b.datetime_start.split("T")[1].substring(0, 5);

    if (!inRange(date)) continue;
    if (!validStatuses.includes(b.status)) continue;
    if (!isInTimeRange(timeStr)) continue;
    increment(date, weekdays[(date.getUTCDay() + 6) % 7], "booker");
  }
  const validCoPlayerStatuses = [
    "PAID",
    "PAID_NOT_CHECKED_IN",
    "CONFIRMED",
    "CHECKED_IN",
  ];
  for (const c of coPlayerBookings) {
    const date = new Date(c.date);
    if (!inRange(date)) continue;
    if (!validCoPlayerStatuses.includes(c.status)) continue;
    increment(date, weekdays[(date.getUTCDay() + 6) % 7], "participant");
  }
  const validActivityStatuses = [
    "PAID_NOT_CHECKED_IN",
    "PAID",
    "CHECKED_IN",
    "CONFIRMED",
  ];
  for (const a of activityBookings) {
    const date = new Date(a.activity.datetime_start);

    if (!inRange(date)) continue;

    if (!validActivityStatuses.includes(a.status)) continue;

    increment(date, weekdays[(date.getUTCDay() + 6) % 7], "activity");
  }

  // --- Fill zeroes for all days within span ---
  const daysInRange: Date[] = [];
  for (
    let d = new Date(start);
    d <= end;
    d = new Date(d.getTime() + 86400000)
  ) {
    daysInRange.push(d);
  }

  for (const d of daysInRange) {
    const { isoYear, isoWeek } = getISOYearWeek(d);
    const key = `${isoYear}-${isoWeek}`;
    const weekday = weekdays[(d.getUTCDay() + 6) % 7];

    weekMap[key] ??= {} as any;
    weekMap[key][weekday] ??= {
      booker: 0,
      participant: 0,
      activity: 0,
    };
  }

  const sortedWeeks = Object.keys(weekMap)
    .map((k) => {
      const [y, w] = k.split("-").map(Number);
      return { y, w };
    })
    .sort((a, b) => a.y - b.y || a.w - b.w);

  const first = sortedWeeks[0];
  const last = sortedWeeks[sortedWeeks.length - 1];

  const weekRangeLabel =
    first.w === last.w
      ? `${first.y} v. ${first.w}`
      : `${first.y} v. ${first.w} - ${last.y} v. ${last.w}`;

  // --- Create weekly rows ---
  const rows: MemberOccupancyRow[] = Object.entries(weekMap).map(
    ([weekNum, days]) => {
      const total = { booker: 0, participant: 0, activity: 0 };
      const row: MemberOccupancyRow & { _sortKey?: string } = {
        __typename: "MemberOccupancyRow",
        type: "row",
        name: member_id + " - " + member_name,
        memberType,
        timespan: `${weekNum.split("-")[0]} v. ${weekNum.split("-")[1]}`,
        _sortKey: weekNum,
        monday: {},
        tuesday: {},
        wednesday: {},
        thursday: {},
        friday: {},
        saturday: {},
        sunday: {},
        total: {},
      };

      for (const day of weekdays) {
        const dayIncluded = weekdayCounts[day] > 0;
        const cell = days[day] ?? { booker: 0, participant: 0, activity: 0 };

        if (!dayIncluded) {
          row[day] = {
            booker: null,
            participant: null,
            activity: null,
            total: null,
          };
          continue;
        }

        const totalForDay =
          (cell.booker ?? 0) + (cell.participant ?? 0) + (cell.activity ?? 0);
        row[day] = { ...cell, total: totalForDay };

        total.booker += cell.booker;
        total.participant += cell.participant;
        total.activity += cell.activity;
      }

      row.total = {
        booker: total.booker,
        participant: total.participant,
        activity: total.activity,
        total: total.booker + total.participant + total.activity,
      };

      return row;
    }
  );
  rows.sort((a: any, b: any) => {
    if (!a._sortKey || !b._sortKey) return 0;

    const [ay, aw] = a._sortKey.split("-").map(Number);
    const [by, bw] = b._sortKey.split("-").map(Number);

    return ay - by || aw - bw;
  });

  // --- Total row ---
  // --- Total row ---
  if (rows.length > 0) {
    let weekRange;
    const totalRow: MemberOccupancyRow = {
      __typename: "MemberOccupancyRow",
      type: "total_summary",
      name: `${member_name} - total`,
      memberType,
      timespan: weekRangeLabel,
      monday: {},
      tuesday: {},
      wednesday: {},
      thursday: {},
      friday: {},
      saturday: {},
      sunday: {},
      total: {},
    };

    const weekdaysArr = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ] as const;

    const total = { booker: 0, participant: 0, activity: 0, total: 0 };

    for (const day of weekdaysArr) {
      // skip days not selected
      if (weekdayCounts[day] <= 0) {
        totalRow[day] = {
          booker: null,
          participant: null,
          activity: null,
          total: null,
        };
        continue;
      }

      const sum = { booker: 0, participant: 0, activity: 0, total: 0 };
      for (const r of rows) {
        const c = r[day];
        if (!c || c.booker === null) continue;
        sum.booker += c.booker ?? 0;
        sum.participant += c.participant ?? 0;
        sum.activity += c.activity ?? 0;
      }
      sum.total = sum.booker + sum.participant + sum.activity;
      totalRow[day] = sum;

      total.booker += sum.booker;
      total.participant += sum.participant;
      total.activity += sum.activity;
      total.total += sum.total;
    }

    totalRow.total = total;

    // Put the total row at the top
    rows.unshift(totalRow);
  }

  // --- Return final structure ---
  return {
    __typename: "MemberStatisticsExport",
    generatedAt: new Date().toISOString(),
    count: rows.length,
    data: rows,
  };
};

export default createRowsForSpecificMember;
